/**
 * product-page.js — Ajax Add-to-Cart + Dynamic Price Updates
 * Custom Element: <product-page>
 *
 * 1. Intercepts form submit → POST /cart/add.js → dispatches cart:open
 * 2. Listens for variant radio changes → updates displayed prices
 * 3. Listens for selling-plan radio changes → recalculates sub prices
 */
class ProductPage extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form[action*="/cart/add"]');
    if (!this.form) return;

    this.submitBtn = this.form.querySelector('[type="submit"]');
    this.submitText = this.submitBtn?.textContent?.trim() || '';

    /* Parse product data injected by Liquid */
    const dataEl = this.querySelector('[data-product-data]');
    this.productData = dataEl ? JSON.parse(dataEl.textContent) : null;

    /* Price display elements */
    this.onetimePrice = this.querySelector('[data-onetime-price]');
    this.onetimeCompare = this.querySelector('[data-onetime-compare]');
    this.subCompares = this.querySelectorAll('[data-sub-compare]');
    this.subPrices = this.querySelectorAll('[data-sub-price]');
    this.finalPrice = this.querySelector('[data-final-price]');
    this.finalCompare = this.querySelector('[data-final-compare]');
    this.pricePerWash = this.querySelector('[data-price-per-wash]');
    this.pricePerPack = this.querySelector('[data-price-per-pack]');
    this.savingsBadge = this.querySelector('[data-savings]');
    this.subSavingsBadge = this.querySelector('[data-sub-savings-badge]');

    /* Translation templates (injected via data-attributes from Liquid) */
    this.tPpw = this.dataset.tPpw || 'Nur __PRICE__ pro Waschgang';
    this.tPpp = this.dataset.tPpp || 'nur __PRICE__ pro Packung';
    this.tSavings = this.dataset.tSavings || 'Du sparst __PCT__%';

    /* Mix & Match picker (optional) */
    this.mixMatchPicker = this.querySelector('mix-match-picker');
    if (this.mixMatchPicker) {
      this.mixMatchPicker.addEventListener('mix-match:valid', () => {
        this.updateSubmitState();
      });
      this.mixMatchPicker.addEventListener('mix-match:invalid', () => {
        this.updateSubmitState();
      });
    }

    this.form.addEventListener('submit', (e) => this.onSubmit(e));
    this.bindPriceUpdates();

    /* Disable submit initially if mix-match is incomplete */
    if (this.mixMatchPicker) {
      this.updateSubmitState();
    }
  }

  /* ── Price Update Binding ── */

  bindPriceUpdates() {
    if (!this.productData) return;

    /* Variant radios (Step 2) */
    this.form.querySelectorAll('input[name="id"]').forEach((radio) => {
      radio.addEventListener('change', () => this.updatePrices());
    });

    /* Selling plan radios (Onetime + Subscription card) */
    this.form.querySelectorAll('input[name="selling_plan"]').forEach((radio) => {
      radio.addEventListener('change', () => this.updatePrices());
    });

    /* Interval Pills (6/12 Monate) — wechseln Plan-ID innerhalb Abo-Card */
    this.form.querySelectorAll('[data-delivery-interval]').forEach((pill) => {
      pill.addEventListener('click', (e) => this.onIntervalClick(e));
    });
  }

  /* ──────────────────────────────────────────────────────────────
     Interval-Pill Click — wechselt aktive Plan-ID innerhalb Abo-Card.
     stopPropagation verhindert Label-Click → vermeidet doppelten
     updatePrices()-Trigger. Markiert Abo-Card als gewählt, falls
     vorher OneTime aktiv war.
     ────────────────────────────────────────────────────────────── */
  onIntervalClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const pill = e.currentTarget;
    const planId = pill.dataset.planId;
    if (!planId) return;

    const subInput = this.form.querySelector('[data-delivery-subscription-input]');
    if (!subInput) return;

    /* 1. Pills toggle: nur die geklickte ist aktiv */
    this.form.querySelectorAll('[data-delivery-interval]').forEach((p) => {
      const isActive = p === pill;
      p.classList.toggle('product__delivery-interval--active', isActive);
      p.setAttribute('aria-checked', String(isActive));
    });

    /* 2. Hidden Subscription-Input: neuer Plan-Wert + checked aktivieren */
    subInput.value = planId;
    subInput.checked = true;

    /* 3. Change-Event manuell triggern → updatePrices() läuft genau 1× */
    subInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  updatePrices() {
    const selectedVariantId = parseInt(
      this.form.querySelector('input[name="id"]:checked')?.value,
      10
    );

    const variant = this.productData.variants.find(
      (v) => v.id === selectedVariantId
    );
    if (!variant) return;

    const variantPrice = variant.price;
    const comparePrice = variant.compare_at_price;

    /* Update one-time price display (price + compare_at_price) */
    if (this.onetimePrice) {
      this.onetimePrice.textContent = this.formatMoney(variantPrice);
    }
    if (this.onetimeCompare) {
      if (comparePrice && comparePrice > variantPrice) {
        this.onetimeCompare.textContent = this.formatMoney(comparePrice);
        this.onetimeCompare.style.display = '';
      } else {
        this.onetimeCompare.textContent = '';
        this.onetimeCompare.style.display = 'none';
      }
    }

    /* ── Subscription-Card (1 Card + Pills) ── */
    const variantPlans = variant.selling_plans || [];
    const subInput = this.form.querySelector('[data-delivery-subscription-input]');
    const subCard = subInput?.closest('.product__delivery-option');
    const intervalPills = this.form.querySelectorAll('[data-delivery-interval]');

    if (subInput && variantPlans.length > 0) {
      if (subCard) subCard.style.display = '';

      /* Sync Pills mit den Plan-IDs der aktuellen Variante (können pro Variante differieren).
         Aktiver Pill = der mit Plan-ID == subInput.value, oder erste Pill als Fallback. */
      let activeIdx = -1;
      const checkedPlanValue = subInput.value;

      intervalPills.forEach((pill, i) => {
        const plan = variantPlans[i];
        if (!plan) {
          pill.style.display = 'none';
          return;
        }
        pill.style.display = '';
        pill.dataset.planId = plan.id;
        pill.dataset.planPrice = plan.price;

        if (String(plan.id) === String(checkedPlanValue)) {
          activeIdx = i;
        }
      });

      /* Fallback wenn aktueller Plan in neuer Variante nicht existiert */
      if (activeIdx === -1) {
        activeIdx = 0;
        if (variantPlans[0]) subInput.value = variantPlans[0].id;
      }

      intervalPills.forEach((pill, i) => {
        const active = i === activeIdx;
        pill.classList.toggle('product__delivery-interval--active', active);
        pill.setAttribute('aria-checked', String(active));
      });

      /* Sub-Card-Preise: aktueller aktiver Plan */
      const activePlan = variantPlans[activeIdx];
      const subPriceEl = this.form.querySelector('[data-sub-price]');
      const subCompareEl = this.form.querySelector('[data-sub-compare]');
      if (subPriceEl && activePlan) {
        subPriceEl.textContent = this.formatMoney(activePlan.price);
      }
      if (subCompareEl && activePlan) {
        if (activePlan.price < variantPrice) {
          subCompareEl.textContent = this.formatMoney(variantPrice);
          subCompareEl.style.display = '';
        } else {
          subCompareEl.style.display = 'none';
        }
      }
    } else if (subCard) {
      /* Variante hat keine Plans → Abo-Card komplett verbergen */
      subCard.style.display = 'none';
    }

    /* Update final price (bottom summary) */
    const checkedPlanValue = this.form.querySelector(
      'input[name="selling_plan"]:checked'
    )?.value;
    const isSubscription = checkedPlanValue && checkedPlanValue !== '';
    let finalAmount = variantPrice;

    if (isSubscription) {
      const activePlan = variantPlans.find(
        (p) => p.id === parseInt(checkedPlanValue, 10)
      );
      if (activePlan) {
        finalAmount = activePlan.price;
      }
    }

    if (this.finalPrice) {
      this.finalPrice.textContent = this.formatMoney(finalAmount);
    }

    /* Update final compare-price (durchgestrichener Original-Preis).
       Bei Abo: originalPrice = variantPrice (Rabatt kommt vom Plan).
       Bei Onetime + reduzierter Variant: originalPrice = compare_at_price. */
    if (this.finalCompare) {
      const originalPrice = isSubscription
        ? variantPrice
        : (comparePrice && comparePrice > variantPrice ? comparePrice : null);
      if (originalPrice && originalPrice > finalAmount) {
        this.finalCompare.textContent = this.formatMoney(originalPrice);
        this.finalCompare.hidden = false;
      } else {
        this.finalCompare.textContent = '';
        this.finalCompare.hidden = true;
      }
    }

    /* Update price-per-wash */
    if (this.pricePerWash && variant.washes) {
      const ppw = Math.round(finalAmount / variant.washes);
      this.pricePerWash.textContent = this.tPpw.replace('__PRICE__', this.formatMoney(ppw));
    }

    /* Update price-per-pack — sichtbar wenn ein Rabatt aktiv UND Variant mehr als 1 Packung
       (sonst redundant zum Final-Preis). Rabatt-Quelle: Abo ODER Mehrpack-Variant (3+1, 5+3). */
    if (this.pricePerPack) {
      const hasDiscount = isSubscription || (comparePrice && comparePrice > variantPrice);
      if (hasDiscount && variant.packs && variant.packs > 1) {
        const ppp = Math.round(finalAmount / variant.packs);
        this.pricePerPack.textContent = this.tPpp.replace('__PRICE__', this.formatMoney(ppp));
        this.pricePerPack.classList.remove('product__price-per-pack--hidden');
      } else {
        this.pricePerPack.classList.add('product__price-per-pack--hidden');
      }
    }

    /* Update savings badge */
    if (this.savingsBadge) {
      const referencePrice = comparePrice || variantPrice;
      if (referencePrice > finalAmount) {
        const pct = Math.round(((referencePrice - finalAmount) / referencePrice) * 100);
        this.savingsBadge.textContent = this.tSavings.replace('__PCT__', pct);
        this.savingsBadge.classList.remove('product__price-savings--hidden');
      } else {
        this.savingsBadge.textContent = '';
        this.savingsBadge.classList.add('product__price-savings--hidden');
      }
    }

    /* Update Sub-Savings Badge ("Spare X %") in der Abo-Card.
       Berechnet Total-Ersparnis vs. Compare-Preis (Originalpreis) basierend auf
       aktivem Sub-Preis. Konsistent mit Liquid initial render. */
    if (this.subSavingsBadge) {
      const subRef = comparePrice && comparePrice > variantPrice ? comparePrice : variantPrice;
      const activeSubPlan = variantPlans.find(
        (p) => p.id === parseInt(this.form.querySelector('[data-delivery-subscription-input]')?.value, 10)
      );
      const subPrice = activeSubPlan ? activeSubPlan.price : null;
      if (subPrice && subPrice < subRef) {
        const subPct = Math.round(((subRef - subPrice) / subRef) * 100);
        this.subSavingsBadge.textContent = 'Spare ' + subPct + '%';
      }
    }

    /* Update mix-match picker when variant changes (different pack count) */
    if (this.mixMatchPicker && variant.packs) {
      this.mixMatchPicker.setTotalPacks(variant.packs);
    }

    /* Update submit button availability */
    this.updateSubmitState(variant);
  }

  updateSubmitState(variant) {
    if (!this.submitBtn) return;

    /* Find current variant if not passed */
    if (!variant && this.productData) {
      const selectedId = parseInt(
        this.form.querySelector('input[name="id"]:checked')?.value,
        10
      );
      variant = this.productData.variants.find((v) => v.id === selectedId);
    }

    const variantAvailable = variant ? variant.available : true;
    const mixMatchValid = this.mixMatchPicker
      ? this.mixMatchPicker.isValid()
      : true;

    this.submitBtn.disabled = !variantAvailable || !mixMatchValid;
  }

  calcSubPrice(variantPrice, plan) {
    if (plan.value_type === 'percentage') {
      return Math.round(variantPrice * (100 - plan.value) / 100);
    }
    if (plan.value_type === 'fixed_amount') {
      return Math.max(0, variantPrice - plan.value);
    }
    if (plan.value_type === 'price') {
      return plan.value;
    }
    return variantPrice;
  }

  formatMoney(cents) {
    return '€' + (cents / 100).toFixed(2).replace('.', ',');
  }

  /* ── Add to Cart ── */

  async onSubmit(e) {
    e.preventDefault();
    if (this.submitBtn?.disabled) return;

    const variantId = this.form.querySelector('input[name="id"]:checked')?.value;
    if (!variantId) return;

    const sellingPlan =
      this.form.querySelector('input[name="selling_plan"]:checked')?.value || '';

    const body = { id: parseInt(variantId, 10), quantity: 1 };
    if (sellingPlan) body.selling_plan = parseInt(sellingPlan, 10);

    /* Mix & Match: collect fragrance properties */
    if (this.mixMatchPicker) {
      if (!this.mixMatchPicker.isValid()) return;
      body.properties = this.mixMatchPicker.getProperties();
    }

    this.setLoading(true);

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || res.status);
      }

      /* Sprint 019d #17: Success-Feedback — 350ms grün-Bounce vor Cart-Drawer öffnet.
         User sieht dass Add-to-Cart erfolgreich war (Baymard: reduziert Doppel-Klicks um 60%). */
      this.setAdded(true);
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('cart:open'));
      }, 350);
    } catch (err) {
      console.error('[product-page] Add to cart error:', err);
      this.form.submit();
    } finally {
      /* Success-State bleibt sichtbar bis Cart-Drawer öffnet — dann Reset erst nach Close */
      setTimeout(() => this.setLoading(false), 400);
    }
  }

  /* Sprint 019d #17: Instant-Feedback via State-Classes + Text-Swap.
     Klassen: is-loading (pulse), is-added (grün + "IM WARENKORB ✓")
     Text: Loading = "…", Added = "IM WARENKORB ✓", Default = original label. */
  setLoading(loading) {
    if (!this.submitBtn) return;
    this.submitBtn.disabled = loading;
    this.submitBtn.classList.toggle('is-loading', loading);
    const labelSpan = this.submitBtn.querySelector('[data-atc-label]');
    if (labelSpan) {
      labelSpan.textContent = loading ? '…' : this.submitText;
    } else {
      this.submitBtn.textContent = loading ? '…' : this.submitText;
    }
    if (!loading) {
      /* Reset added-State beim Loading-Ende (nach kurzer Anzeige) */
      setTimeout(() => this.submitBtn.classList.remove('is-added'), 200);
    }
  }

  setAdded(added) {
    if (!this.submitBtn) return;
    this.submitBtn.classList.remove('is-loading');
    this.submitBtn.classList.toggle('is-added', added);
    const labelSpan = this.submitBtn.querySelector('[data-atc-label]');
    const label = added ? '✓ HINZUGEFÜGT' : this.submitText;
    if (labelSpan) {
      labelSpan.textContent = label;
    } else {
      this.submitBtn.textContent = label;
    }
  }
}

customElements.define('product-page', ProductPage);
